import { Component, EventEmitter, Input, Output } from '@angular/core';
import { environment } from 'src/environments/environment';

/** Plan IDs from subscription-manager (Globot): BASE=3, PREMIUM=4, TEST=5 */
export const PLAN_ID_BASE = 3;
export const PLAN_ID_PREMIUM = 4;
export const PLAN_ID_TEST = 5;

@Component({
  selector: 'globot-type-subscription-plans',
  templateUrl: './type-subscription-plans.component.html',
  styleUrls: ['./type-subscription-plans.component.scss'],
})
export class TypeSubscriptionPlansComponent {
  @Input() currentPlanAlias: string | undefined;
  /** When false (e.g. subscription cancelled), do not show downgrade option. */
  @Input() allowDowngrade: boolean = true;
  /** True when user has an active paid period (enables upgrade/prorrateo). */
  @Input() hasActivePaidPeriod: boolean = false;
  /** True when user has a saved payment method (enables quick reactivation when expired). */
  @Input() hasPaymentMethod: boolean = false;
  @Output() upgradeClick = new EventEmitter<number>();
  @Output() downgradeClick = new EventEmitter<number>();
  @Output() reactivateClick = new EventEmitter<number>();

  plans = [
    {
      class: 'tier2',
      price: 'CLP $12.990',
      denominator: '/ Mes',
      title: 'Standard',
      planId: PLAN_ID_BASE,
      description:
        'Para negocios que quieren automatizar su atención al cliente en todos los canales.',
      features: [
        '2 asistentes',
        '1.000 mensajes al mes',
        'Todos los canales',
        'Equipo hasta 3 personas',
      ],
      link: this.encodeSubscriptionPlan('BASE'),
      buttonText: 'Suscribirse',
      showIvaIncluded: true,
    },
    {
      class: 'tier3',
      price: 'CLP $29.990',
      denominator: '/ Mes',
      title: 'Premium',
      planId: PLAN_ID_PREMIUM,
      description: 'Para negocios en crecimiento que necesitan más capacidad y personalización.',
      features: [
        '5 asistentes',
        '5.000 mensajes al mes',
        'Todos los canales',
        'Equipo hasta 10 personas',
      ],
      link: this.encodeSubscriptionPlan('PREMIUM'),
      buttonText: 'Suscribirse',
      showIvaIncluded: true,
    },
    {
      class: 'tier3',
      price: 'CLP $1.000',
      denominator: '/ Semana',
      title: 'Plan Prueba',
      planId: PLAN_ID_TEST,
      description: 'Plan solo para pruebas: 1.000 CLP por semana.',
      features: ['2 asistentes', '1.000 mensajes', 'Integración Web y Wordpress'],
      link: this.encodeSubscriptionPlan('TEST'),
      buttonText: 'Suscribirse',
      showIvaIncluded: true,
    },
    {
      class: 'tier4',
      price: 'A convenir',
      title: 'Empresa',
      description: 'Pensado para organizaciones que requieran soluciones avanzadas.',
      features: [
        'Asistentes ilimitados',
        'Mensajes ilimitados',
        'Integraciones a medida',
        'Soporte dedicado',
      ],
      link: 'https://globot.ai/#contacto',
      buttonText: 'Contáctanos',
      marginBottom: '1rem',
      showIvaIncluded: false,
    },
  ];

  constructor() {
    // TEST plan ("Plan Prueba", id=5) is QA-only; hide it unless the env flag is on.
    // Mirrors subscription-manager's TEST_PLAN_ENABLED (server-side hides/blocks it too).
    if (!environment.showTestPlan) {
      this.plans = this.plans.filter((plan) => plan.planId !== PLAN_ID_TEST);
    }
  }

  private encodeSubscriptionPlan(plan: string): string {
    return '/subscription/login?subscriptionPlan=' + btoa(plan);
  }

  /** True if this plan is an upgrade from current plan (e.g. Standard -> Premium). */
  isUpgrade(plan: { planId?: number }): boolean {
    if (!this.currentPlanAlias || !plan.planId || !this.hasActivePaidPeriod) return false;
    const alias = (this.currentPlanAlias || '').toLowerCase();
    if (alias === 'standard' && plan.planId === PLAN_ID_PREMIUM) return true;
    if (alias === 'gratuito' && (plan.planId === PLAN_ID_BASE || plan.planId === PLAN_ID_PREMIUM))
      return true;
    if (
      alias === 'plan prueba' &&
      (plan.planId === PLAN_ID_BASE || plan.planId === PLAN_ID_PREMIUM)
    )
      return true;
    return false;
  }

  /** True if this plan is a downgrade from current (e.g. Premium -> Standard). */
  isDowngrade(plan: { planId?: number }): boolean {
    if (!this.currentPlanAlias || !plan.planId || !this.allowDowngrade) return false;
    const alias = (this.currentPlanAlias || '').toUpperCase();
    return alias === 'PREMIUM' && plan.planId === PLAN_ID_BASE;
  }

  /** True when user can reactivate with saved card (expired, has payment method, this is a paid plan). */
  isReactivateTarget(plan: { planId?: number }): boolean {
    return !this.hasActivePaidPeriod && this.hasPaymentMethod === true && !!plan.planId;
  }

  /** True if this plan is the user's current plan (button should be disabled). */
  isCurrentPlan(plan: { planId?: number; title?: string }): boolean {
    if (!this.currentPlanAlias) return false;
    const alias = (this.currentPlanAlias || '').toLowerCase();
    if (plan.planId === PLAN_ID_BASE && alias === 'standard') return true;
    if (plan.planId === PLAN_ID_PREMIUM && alias === 'premium') return true;
    if (plan.planId === PLAN_ID_TEST && alias === 'plan prueba') return true;
    if (!plan.planId && (alias === 'empresa' || alias === 'personalizado')) return true;
    return false;
  }

  onPlanAction(plan: { planId?: number; link: string }): void {
    if (this.hasActivePaidPeriod && this.isUpgrade(plan) && plan.planId) {
      this.upgradeClick.emit(plan.planId);
    } else if (!this.hasActivePaidPeriod && this.hasPaymentMethod && plan.planId) {
      this.reactivateClick.emit(plan.planId);
    } else if (this.isDowngrade(plan) && plan.planId) {
      this.downgradeClick.emit(plan.planId);
    }
  }

  showTooltip: boolean = false;
  toggleTooltip() {
    this.showTooltip = !this.showTooltip;
  }
}
